import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useCalculationBaseColumnLabels } from "hooks/useCalculationBaseColumnLabels";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ChannelConsolidatedSalesData, ChannelSales } from "store/analyticsStore";
import type { TFetchStatus } from "store/types";

import EmptyState from "../EmptyState";
import TableRowsSkeleton from "../TableRowsSkeleton";
import CategoryRow from "./CategoryRow";
import styles from "./style.module.scss";

interface ITitleConsolidatedSales {
    /**
     * Object with data per category.
     * Key is category kind. Values is an array with sales data per channel.
     */
    categoriesData: Record<number, ChannelSales[]> | undefined;
    /**
     * Status of request.
     */
    dataStatus: TFetchStatus;
}

export interface ICategoryTotalValues {
    quantities: number;
    turnover: number;
    kind: number;
}

/**
 * Table with consolidated sales data per category (and then channels in that category).
 */
const TitleConsolidatedSales = ({ categoriesData, dataStatus }: ITitleConsolidatedSales) => {
    const [t] = useTranslation();
    const { resolveLabel } = useCalculationBaseColumnLabels();
    const dataLoading = ["idle", "pending"].includes(dataStatus);

    /**
     * Formats values for categories total data.
     */
    const categoriesTotalValues: ICategoryTotalValues[] = useMemo(() => {
        if (!categoriesData) return [];
        return Object.entries(categoriesData).map(([kind, channels]) => {
            return channels.reduce(
                (total, channel) => {
                    total.quantities += (channel.data as ChannelConsolidatedSalesData).total_channel_id_quantities ?? 0;
                    total.turnover += (channel.data as ChannelConsolidatedSalesData).total_channel_id_turnover ?? 0;
                    return total;
                },
                { quantities: 0, turnover: 0, kind: +kind },
            );
        });
    }, [categoriesData]);

    return (
        <TableContainer className={styles["title-consolidated-sales-container"]} component={Paper} elevation={0}>
            <Table>
                <TableHead className={styles["table-head"]}>
                    <TableRow>
                        <TableCell>{t("TitleSalesTable.ConsolidatedTable.ChannelCategory")}</TableCell>
                        <TableCell align="right">{resolveLabel("turnover")}</TableCell>
                        <TableCell align="right">{resolveLabel("quantity")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {dataLoading ? (
                        <TableRowsSkeleton nbColumns={3} nbRows={5} />
                    ) : !categoriesData ? (
                        <TableRow>
                            <TableCell colSpan={3} align="center">
                                <div className={styles["empty-state"]}>
                                    <EmptyState type="titleConsolidatedSales" />
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        <CategoryRow categoriesData={categoriesData} totalValues={categoriesTotalValues} />
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default TitleConsolidatedSales;
